import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { DEFAULT_FILTER } from '../../../../shared/constants/constants';
import { FilterModel } from '../../../../shared/interface/interface';
import { CategoryModel } from '../../../../shared/models/category.model';
import { CategoryService } from '../../../../shared/services/category.service';
import { ScrollDisableService } from '../../../../shared/services/scroll-disable.service';
import { SharedModule } from '../../../../shared/shared.module';
import { AddEditCategoryComponent } from '../add-edit-category/add-edit-category.component';

@Component({
  selector: 'app-categories',
  standalone: true,
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.scss',
  imports: [
    SharedModule,
    ConfirmationModalComponent,
    SkeletonLoaderComponent,
    PageHeaderComponent,
    ModalComponent,
    AddEditCategoryComponent,
  ],
})
export class CategoriesComponent implements OnInit {
  categories: CategoryModel[] = [];
  deleteCategoryId!: string | undefined;
  isLoading: boolean = false;
  categoryCount: number = 0;
  deleteLoading: boolean = false;
  showCategoryModal: boolean = false;
  editCategory: CategoryModel | null = null;
  searchForm = this.fb.group({ searchText: [''] });
  subscription: Subscription = new Subscription();
  private categoryFilterSubject$: BehaviorSubject<FilterModel> =
    new BehaviorSubject<FilterModel>(DEFAULT_FILTER);
  categoryFilter = this.categoryFilterSubject$.asObservable();

  constructor(
    private categoryService: CategoryService,
    private toastr: ToastrService,
    private scrollDisableService: ScrollDisableService,
    private fb: FormBuilder
  ) {}
  ngOnInit(): void {
    this.subscribeToFilter();
  }

  getCategoryList(filter: FilterModel) {
    this.isLoading = true;
    this.categoryService.getCategoryList(filter).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.categories =
          filter.pageNumber > 1
            ? [...this.categories, ...res.result]
            : res.result;
        this.categoryCount = res.count;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(
          err?.error?.message || err.message || 'Something went wrong!'
        );
      },
    });
  }
  deleteBlog() {
    if (this.deleteCategoryId) {
      this.deleteLoading = true;
      this.categoryService.deleteCategory(this.deleteCategoryId).subscribe({
        next: (res) => {
          this.toastr.success('Category deleted successfully.');
          this.updateCategoryFilter(DEFAULT_FILTER);
          this.deleteCategoryId = '';
          this.scrollDisableService.addScroll();
          this.deleteLoading = false;
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.scrollDisableService.addScroll();
          this.deleteLoading = false;
        },
      });
    }
  }
  cancelDelete() {
    this.deleteCategoryId = undefined;
    this.scrollDisableService.addScroll();
  }
  onDeleteCategory(id: string) {
    this.deleteCategoryId = id;
    this.scrollDisableService.stopScroll();
  }

  toggleCategoryModal(state: boolean) {
    this.showCategoryModal = state;
    if (!state) {
      this.editCategory = null;
    }
  }

  handleEditCategory(category: CategoryModel) {
    this.editCategory = category;
    this.toggleCategoryModal(true);
  }
  handleUpdate() {
    this.toggleCategoryModal(false);
    this.updateCategoryFilter({
      ...this.categoryFilterSubject$.value,
      pageNumber: 1,
    });
  }

  handleSearch(searchText: string) {
    if (searchText.length === 0 || searchText?.trim()) {
      this.updateCategoryFilter({
        ...this.categoryFilterSubject$.value,
        pageNumber: 1,
        searchText: searchText,
      });
    }
  }

  subscribeToFilter() {
    this.categoryFilterSubject$.subscribe((value) => {
      this.getCategoryList(value);
    });
  }

  updateCategoryFilter(filter: FilterModel) {
    this.categoryFilterSubject$.next(filter);
  }

  loadMoreIfLastIndex() {
    const { searchText, pageNumber, limit } = this.categoryFilterSubject$.value;
    if (pageNumber * limit < this.categoryCount) {
      this.updateCategoryFilter({
        searchText,
        limit,
        pageNumber: pageNumber + 1,
      });
    }
  }
}
